import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import { i18n } from '../../../i18n-handler/handler/i18n';
import { IMissionGroupPro } from '../../../interfaces/missions/IMissionGroupPro';
import { getHourFormatFromMinutes } from '../../utils/format.utils';
import { MissionGroupStatusEnum } from '../../../interfaces/misc/MissionGroupStatusEnum';
import { IWorkWindow, UtilityHandler } from '../../../index';

let lang = 'fr';

export type MissionGroupStatusClass = 'in-progress' | 'cancelled' | 'confirmed' | 'waiting' | '';

/**
 * IN_PROGRESS => en cours
 * WAITING => en attente
 * CONFIRMED => confirmée
 * CANCELLED => annulée
 */
function getMissionStatusAndClass(missionGroup: IMissionGroupPro): { status: string; class: string } {
    let _status = '';
    let _class: MissionGroupStatusClass = '';

    if (missionGroup._status === MissionGroupStatusEnum.IN_PROGRESS) {
        _status = `•\u00A0${i18n.t('mission.status.in-progress', { lng: lang })}`;
        _class = 'in-progress';
    } else if (missionGroup._status === MissionGroupStatusEnum.WAITING) {
        _status = `•\u00A0${i18n.t('mission.status.waiting', { lng: lang })}`;
        _class = 'waiting';
    } else if (missionGroup._status === MissionGroupStatusEnum.CONFIRMED) {
        _status = `•\u00A0${i18n.t('mission.status.confirmed', { lng: lang })}`;
        _class = 'confirmed';
    } else if (missionGroup._status === MissionGroupStatusEnum.CANCELLED) {
        _status = `•\u00A0${i18n.t('mission.status.cancelled', { lng: lang })}`;
        _class = 'cancelled';
    } else {
        // This is a fallback status in case the API sends
        missionGroup._status = MissionGroupStatusEnum.WAITING;
        _status = `•\u00A0${i18n.t('mission.status.waiting', { lng: lang })}`;
        _class = 'waiting';
    }

    return { status: _status, class: _class };
}

function getJobText(missionGroup: IMissionGroupPro): string | undefined {
    if (missionGroup && missionGroup.jobs && missionGroup.jobs.length >= 1) {
        if (missionGroup.jobs[0].category && missionGroup.jobs[0].category.name) {
            return `${missionGroup.jobs[0].category.name} - ${missionGroup.jobs[0].name}`;
        }
        return `${missionGroup.jobs[0].name}`;
    }
}

function countWorkingDays(workWindows: IWorkWindow[]): number {
    const utilityHandler = new UtilityHandler();
    const displayedWorkWindows = utilityHandler.missions.workWindow.getDisplayableWorkWindows(workWindows ?? []);

    return displayedWorkWindows
        .map((window) => window.schedule[0].startTime)
        .filter((date, index, arr) => {
            return arr.findIndex((elem) => isSameDay(date, elem)) === index;
        }).length;
}

function getDatesText(missionGroup: IMissionGroupPro): string | undefined {
    if (missionGroup?.workWindows !== undefined && missionGroup.workWindows.length > 0) {
        const datesNUmber = countWorkingDays(missionGroup.workWindows);
        const startDayDate = new Date(missionGroup.workWindows[0].startTime);
        const endDayDate = new Date(missionGroup.workWindows[missionGroup.workWindows.length - 1].endTime);
        if (isSameDay(startDayDate, endDayDate)) {
            return `${format(startDayDate, 'dd/MM/yyyy')}`;
        } else {
            return `${format(startDayDate, 'dd/MM')} - ${format(endDayDate, 'dd/MM/yyyy')} (${datesNUmber} ${i18n.t(
                'mission.details.days',
                { lng: lang }
            )})`;
        }
    }
    return '';
}

function getHoursText(missionGroup: IMissionGroupPro): string {
    return getHourFormatFromMinutes(missionGroup.totalOfMinutes);
}

function getLanguagesText(missionGroup: IMissionGroupPro): string | undefined {
    let languagesText = '';
    if (missionGroup && missionGroup.languages && missionGroup.languages.length >= 1) {
        languagesText = missionGroup.languages.map((language) => language.name).join(', ');
    } else {
        languagesText = '-';
    }

    return languagesText;
}

function getSoftwaresText(missionGroup: IMissionGroupPro): string | undefined {
    let softwaresText = '';
    if (missionGroup && missionGroup.softwares && missionGroup.softwares.length >= 1) {
        softwaresText = missionGroup.softwares.map((software) => software.name).join(', ');
    } else {
        softwaresText = '-';
    }

    return softwaresText;
}

function getHasSoftware(missionGroup: IMissionGroupPro): boolean {
    let hasSoftware = false;
    missionGroup.softwares && missionGroup.softwares.length > 0 ? (hasSoftware = true) : '';
    return hasSoftware;
}

function getHasLanguage(missionGroup: IMissionGroupPro): boolean {
    let hasLanguage = false;
    missionGroup.languages && missionGroup.languages.length > 0 ? (hasLanguage = true) : '';
    return hasLanguage;
}

function getHasDetails(missionGroup: IMissionGroupPro): boolean {
    let hasDetails = false;
    missionGroup.details && missionGroup.details.length > 0 ? (hasDetails = true) : '';
    return hasDetails;
}

export type ProFormatReturn = {
    getMissionStatusAndClass: Function;
    getJobText: Function;
    getDatesText: Function;
    getHoursText: Function;
    getLanguagesText: Function;
    getSoftwaresText: Function;
    getHasSoftware: Function;
    getHasLanguage: Function;
    getHasDetails: Function;
    countWorkingDays: Function;
};

export const proFormat = (_lang: string): ProFormatReturn => {
    lang = _lang;
    return {
        getMissionStatusAndClass,
        getJobText,
        getDatesText,
        getHoursText,
        getLanguagesText,
        getSoftwaresText,
        getHasSoftware,
        getHasLanguage,
        getHasDetails,
        countWorkingDays,
    };
};
